import { useState, type ReactNode } from 'react';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { Async } from '../components/Async';
import { Sparkline } from '../components/Sparkline';
import { useFetch } from '../lib/useFetch';
import { getPharmacyVolume, refreshReports } from '../lib/api';
import { fmtNum } from '../lib/format';

const ALL = '__all__';

function weekLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function ago(iso?: string): string {
  if (!iso) return '';
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.round(hrs / 24)}d ago`;
}

export const PharmacyVolumePage = ({ tabs }: { tabs?: ReactNode } = {}) => {
  const [weeks, setWeeks] = useState(8);
  const [clinic, setClinic] = useState<string>(ALL);
  const [refreshing, setRefreshing] = useState(false);
  const state = useFetch(() => getPharmacyVolume(weeks), [weeks]);

  async function refresh() {
    setRefreshing(true);
    try {
      await refreshReports(['pharmacy-volume']);
      state.reload();
    } finally {
      setRefreshing(false);
    }
  }

  return (
    <Page>
      <PageHeader
        tabs={tabs}
        title="Pharmacy Volume"
        sub="Items in completed orders, by week and drug (Friday-aligned weeks). New clinics with only pending orders show a pipeline count and bill nothing until orders complete."
        actions={
          <>
            {state.data?.computedAt && (
              <span style={{ fontSize: 12, color: 'var(--p-muted-fg)', alignSelf: 'center' }}>
                Updated {ago(state.data.computedAt)}
              </span>
            )}
            <button className="btn" onClick={refresh} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <select className="select" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))}>
              <option value={8}>Last 8 weeks</option>
              <option value={12}>Last 12 weeks</option>
              <option value={26}>Last 26 weeks</option>
            </select>
          </>
        }
      />

      <Async state={state} isEmpty={(d) => d.clinics.length === 0} emptyLabel="No pharmacy orders in this window.">
        {(data) => {
          const selected = data.clinics.find((c) => c.clinic === clinic);
          // Rows + per-week totals depend on the view (all clinics vs one clinic's drugs).
          const rows = selected
            ? selected.drugs.map((d) => ({ label: d.drug, total: d.total, weekly: d.weekly, pipeline: 0 }))
            : data.clinics.map((c) => ({ label: c.clinic, total: c.total, weekly: c.weekly, pipeline: c.pipelineOrders ?? 0 }));
          const colTotals = (w: string) => rows.reduce((s, r) => s + (r.weekly[w] ?? 0), 0);
          const grandTotal = rows.reduce((s, r) => s + r.total, 0);

          return (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ fontSize: 13, color: 'var(--p-muted-fg)' }}>Clinic:</label>
                <select className="select" value={clinic} onChange={(e) => setClinic(e.target.value)}>
                  <option value={ALL}>All clinics</option>
                  {data.clinics.map((c) => (
                    <option key={c.clinic} value={c.clinic}>
                      {c.clinic} ({fmtNum(c.total)})
                    </option>
                  ))}
                </select>
                <span style={{ fontSize: 12, color: 'var(--p-muted-fg)' }}>
                  {selected ? 'drugs ↓ · weeks →' : 'clinics ↓ · weeks → (pick a clinic to break down by drug)'}
                </span>
              </div>

              <PCard>
                <table className="p-table">
                  <thead>
                    <tr>
                      <th>{selected ? 'Drug' : 'Clinic'}</th>
                      <th>Trend</th>
                      {data.weeks.map((w) => (
                        <th key={w} style={{ textAlign: 'right' }}>{weekLabel(w)}</th>
                      ))}
                      <th style={{ textAlign: 'right' }}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const vals = data.weeks.map((w) => r.weekly[w] ?? 0);
                      // Direction ignores the latest (partial) week.
                      const complete = vals.slice(0, -1);
                      const up =
                        complete.length >= 2 && complete.some((v) => v > 0)
                          ? complete[complete.length - 1] >= complete[0]
                          : null;
                      return (
                        <tr
                          key={r.label}
                          onClick={!selected ? () => setClinic(r.label) : undefined}
                          style={!selected ? { cursor: 'pointer' } : undefined}
                        >
                          <td style={{ fontWeight: 500 }}>
                            {r.label}
                            {r.pipeline > 0 && (
                              <span style={{ fontSize: 11, color: 'var(--p-muted-fg)', fontWeight: 400 }}>
                                {' '}· {fmtNum(r.pipeline)} in pipeline
                              </span>
                            )}
                          </td>
                          <td><Sparkline values={vals} up={up} /></td>
                          {data.weeks.map((w) => (
                            <td key={w} className="tabular muted" style={{ textAlign: 'right' }}>
                              {r.weekly[w] ? fmtNum(r.weekly[w]) : '·'}
                            </td>
                          ))}
                          <td className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(r.total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid var(--p-border)' }}>
                      <td style={{ fontWeight: 600 }}>Total</td>
                      <td />
                      {data.weeks.map((w) => (
                        <td key={w} className="tabular" style={{ textAlign: 'right', fontWeight: 600 }}>{fmtNum(colTotals(w))}</td>
                      ))}
                      <td className="tabular" style={{ textAlign: 'right', fontWeight: 700 }}>{fmtNum(grandTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </PCard>
            </>
          );
        }}
      </Async>
    </Page>
  );
};
